import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	Button,
	Form,
	Input,
	Modal,
	Select,
	Space,
	Switch,
	Table,
	message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridApi, GridReadyEvent } from "ag-grid-community";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import PaginationTable from "../../components/Pagination";
import useLang from "../../hooks/useLang";
import api from "../../utils/axios";
import { fetchZones } from "../../lib/OptionListApi";
import { userRoles } from "../../types/enums";
import { fetchAllUsers } from "../../lib/UserApi";
import classes from "./index.module.css";

interface PickupPoint {
	id: number;
	name: string;
	countryId: number;
	addressLine?: string;
	latitude?: string;
	longitude?: string;
	mapsUrl?: string;
	phonePrimary?: string;
	phoneSecondary?: string;
	workingHours?: string;
	isActive: boolean;
	createdAt?: string;
	updatedAt?: string;
}

interface PickupPointStaff {
	id: number;
	pickupPointId: number;
	userId: number;
	isPrimary: boolean;
	isActive: boolean;
}

interface DriverOption {
	value: number;
	label: string;
}

const PickupPoints: React.FC = () => {
	const { strings } = useLang("pickupPoints");
	const [pickupPoints, setPickupPoints] = useState<PickupPoint[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
	const [editingPickupPoint, setEditingPickupPoint] =
		useState<PickupPoint | null>(null);
	const [countries, setCountries] = useState<DriverOption[]>([]);
	const [drivers, setDrivers] = useState<DriverOption[]>([]);
	const [staffModalVisible, setStaffModalVisible] = useState<boolean>(false);
	const [staffFormVisible, setStaffFormVisible] = useState<boolean>(false);
	const [selectedPickupPoint, setSelectedPickupPoint] =
		useState<PickupPoint | null>(null);
	const [pickupPointStaff, setPickupPointStaff] = useState<
		PickupPointStaff[]
	>([]);
	const [staffLoading, setStaffLoading] = useState<boolean>(false);
	const [editingStaff, setEditingStaff] = useState<PickupPointStaff | null>(
		null
	);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(20);

	const [form] = Form.useForm<PickupPoint>();
	const [staffForm] = Form.useForm<PickupPointStaff>();
	const gridApiRef = useRef<GridApi | null>(null);
	const gridRef = useRef<AgGridReact | null>(null);

	const driverMap = useMemo(() => {
		const map = new Map<number, string>();
		drivers.forEach((driver) => {
			map.set(driver.value, driver.label);
		});
		return map;
	}, [drivers]);

	const countriesMap = useMemo(() => {
		const map = new Map<number, string>();
		countries.forEach((country) => {
			map.set(country.value, country.label);
		});
		return map;
	}, [countries]);

	const staffTableData = useMemo(
		() =>
			pickupPointStaff.map((staffMember) => ({
				...staffMember,
				driverName:
					driverMap.get(staffMember.userId) ??
					strings?.staffModal?.unknownDriver ??
					"Driver",
			})),
		[driverMap, pickupPointStaff, strings?.staffModal?.unknownDriver]
	);

	const fetchPickupPoints = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await api.get<PickupPoint[]>("PickupPoint");
			if (response.status === 200 && response.data) {
				setPickupPoints(response.data);
			} else {
				setPickupPoints([]);
			}
		} catch (error) {
			message.error(
				strings?.messages?.fetchError ?? "Unable to load pickup points."
			);
		} finally {
			setIsLoading(false);
		}
	}, [strings?.messages?.fetchError]);

	const fetchCountries = useCallback(async () => {
		try {
			const zones = await fetchZones();
			if (zones) {
				const countryOptions = zones
					.filter((zone: any) => zone.levelId === 1)
					.map((zone: any) => ({
						value: zone.id,
						label: zone.description,
					}));
				setCountries(countryOptions);
			}
		} catch (error) {
			message.error(
				strings?.messages?.countriesFetchError ??
					"Unable to load countries."
			);
		}
	}, [strings?.messages?.countriesFetchError]);

	const fetchDrivers = useCallback(async () => {
		const users = await fetchAllUsers();
		if (!users) {
			message.error(
				strings?.messages?.driversFetchError ??
					"Unable to load drivers."
			);
			return;
		}
		const driverOptions = users
			.filter((user: any) => user.userRoleId === userRoles.DRIVER)
			.map((driver: any) => ({
				value: driver.id,
				label:
					`${driver.firstName} ${driver.lastName}`.trim() ||
					driver.username ||
					`${strings?.staffModal?.driver ?? "Driver"} ${driver.id}`,
			}));
		setDrivers(driverOptions);
	}, [strings?.messages?.driversFetchError, strings?.staffModal?.driver]);

	const fetchPickupPointStaff = useCallback(
		async (pickupPointId: number) => {
			setStaffLoading(true);
			try {
				const response = await api.get<PickupPointStaff[]>(
					"PickupPoint/select-staff-per-point",
					{
						params: { pickupPointId },
					}
				);
				if (response.status === 200 && response.data) {
					setPickupPointStaff(response.data);
				} else {
					setPickupPointStaff([]);
				}
			} catch (error) {
				message.error(
					strings?.messages?.staffFetchError ??
						"Unable to load pickup point drivers."
				);
			} finally {
				setStaffLoading(false);
			}
		},
		[strings?.messages?.staffFetchError]
	);

	useEffect(() => {
		if (!strings) {
			return;
		}
		fetchPickupPoints();
		fetchCountries();
		fetchDrivers();
	}, [fetchCountries, fetchDrivers, fetchPickupPoints, strings]);

	const handleAddPickupPoint = () => {
		setEditingPickupPoint(null);
		form.resetFields();
		form.setFieldsValue({ isActive: true });
		setIsModalVisible(true);
	};

	const handleEditPickupPoint = (pickupPoint: PickupPoint) => {
		setEditingPickupPoint(pickupPoint);
		form.setFieldsValue({
			...pickupPoint,
		});
		setIsModalVisible(true);
	};

	const closePickupPointModal = () => {
		setIsModalVisible(false);
		setEditingPickupPoint(null);
		form.resetFields();
	};

	const handlePickupPointSubmit = async () => {
		try {
			const values = await form.validateFields();
			const payload = {
				...values,
				countryId: Number(values.countryId),
			};
			const editingPayload = {
				...values,
				countryId: Number(values.countryId),
				id: editingPickupPoint?.id,
			};
			const request = editingPickupPoint
				? api.put(
						`PickupPoint/${editingPickupPoint.id}`,
						editingPayload
				  )
				: api.post("PickupPoint", payload);

			const response = await request;
			if (response.status === 200 || response.status === 201) {
				message.success(
					strings?.messages?.saveSuccess ??
						"Pickup point saved successfully."
				);
				closePickupPointModal();
				fetchPickupPoints();
			}
		} catch (error: any) {
			if (error?.errorFields) {
				return;
			}
			message.error(
				strings?.messages?.saveError ?? "Unable to save pickup point."
			);
		}
	};

	const openStaffModal = async (pickupPoint: PickupPoint) => {
		setSelectedPickupPoint(pickupPoint);
		setStaffModalVisible(true);
		setEditingStaff(null);
		staffForm.resetFields();
		await fetchPickupPointStaff(pickupPoint.id);
	};

	const closeStaffModal = () => {
		setStaffModalVisible(false);
		setSelectedPickupPoint(null);
		setPickupPointStaff([]);
	};

	const handleAddStaff = () => {
		setEditingStaff(null);
		staffForm.resetFields();
		staffForm.setFieldsValue({ isActive: true, isPrimary: false });
		setStaffFormVisible(true);
	};

	const handleEditStaff = (staffMember: PickupPointStaff) => {
		setEditingStaff(staffMember);
		staffForm.setFieldsValue({
			userId: staffMember.userId,
			isPrimary: staffMember.isPrimary,
			isActive: staffMember.isActive,
		});
		setStaffFormVisible(true);
	};

	const closeStaffForm = () => {
		setStaffFormVisible(false);
		setEditingStaff(null);
		staffForm.resetFields();
	};

	const handleStaffSubmit = async () => {
		if (!selectedPickupPoint) {
			return;
		}
		try {
			const values = await staffForm.validateFields();
			const payload = {
				...values,
				pickupPointId: selectedPickupPoint.id,
			};
			const updatePayload = {
				...values,
				pickupPointId: selectedPickupPoint.id,
				id: editingStaff?.id,
			};
			const request = editingStaff
				? api.put(
						`PickupPoint/staff-update${editingStaff.id}`,
						updatePayload
				  )
				: api.post(
						`PickupPoint/staff-insert${selectedPickupPoint.id}`,
						payload
				  );

			const response = await request;
			if (response.status === 200 || response.status === 201) {
				message.success(
					strings?.messages?.staffSaveSuccess ??
						"Pickup point driver saved successfully."
				);
				closeStaffForm();
				await fetchPickupPointStaff(selectedPickupPoint.id);
			}
		} catch (error: any) {
			if (error?.errorFields) {
				return;
			}
			message.error(
				strings?.messages?.staffSaveError ??
					"Unable to save pickup point driver."
			);
		}
	};

	const handleDeleteStaff = (staffMember: PickupPointStaff) => {
		Modal.confirm({
			title: strings?.staffModal?.deleteConfirmTitle ?? "Remove driver",
			content:
				strings?.staffModal?.deleteConfirm ??
				"Are you sure you want to remove this driver from the pickup point?",
			okText: strings?.staffModal?.delete ?? "Delete",
			cancelText: strings?.staffModal?.cancel ?? "Cancel",
			okButtonProps: { danger: true },
			onOk: async () => {
				try {
					const response = await api.delete(
						`PickupPoint/staff-delete${staffMember.id}`
					);
					if (response.status === 200) {
						message.success(
							strings?.messages?.staffDeleteSuccess ??
								"Pickup point driver removed."
						);
						if (selectedPickupPoint) {
							await fetchPickupPointStaff(selectedPickupPoint.id);
						}
					}
				} catch (error) {
					message.error(
						strings?.messages?.staffDeleteError ??
							"Unable to remove pickup point driver."
					);
				}
			},
		});
	};

	const onGridReady = (event: GridReadyEvent) => {
		gridApiRef.current = event.api;
	};

	useEffect(() => {
		if (!gridApiRef.current) {
			return;
		}
		if (isLoading) {
			gridApiRef.current.showLoadingOverlay();
		} else if (pickupPoints.length === 0) {
			gridApiRef.current.showNoRowsOverlay();
		} else {
			gridApiRef.current.hideOverlay();
		}
	}, [isLoading, pickupPoints]);

	const paginatedData = useMemo(() => {
		const startIndex = (currentPage - 1) * pageSize;
		const endIndex = startIndex + pageSize;
		return pickupPoints.slice(startIndex, endIndex);
	}, [currentPage, pageSize, pickupPoints]);

	const totalItems = pickupPoints.length;
	const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
	const endItem = Math.min(currentPage * pageSize, totalItems);

	const handlePageChange = (page: number, size: number) => {
		setCurrentPage(page);
		if (size !== pageSize) {
			setPageSize(size);
		}
	};

	const handlePageSizeChange = (current: number, size: number) => {
		setPageSize(size);
		setCurrentPage(1);
	};

	const columns = useMemo<ColDef[]>(() => {
		return [
			{
				field: "name",
				headerName: strings?.table?.name ?? "Name",
				flex: 1,
				minWidth: 180,
			},
			{
				field: "countryId",
				headerName: strings?.table?.country ?? "Country",
				valueGetter: (params: any) =>
					countriesMap.get(params.data?.countryId) ??
					strings?.table?.unknownCountry ??
					"Country",
				flex: 1,
				minWidth: 100,
			},
			{
				field: "mapsUrl",
				headerName: strings?.table?.address ?? "Address",
				flex: 1,
				minWidth: 150,
			},
			{
				field: "phonePrimary",
				headerName: strings?.table?.primaryPhone ?? "Primary phone",
				minWidth: 160,
			},

			{
				field: "isActive",
				headerName: strings?.table?.active ?? "Active",
				minWidth: 120,
				valueFormatter: (params: any) =>
					params.value
						? strings?.table?.yes ?? "Yes"
						: strings?.table?.no ?? "No",
			},
			{
				field: "actions",
				headerName: strings?.table?.actions ?? "Actions",
				cellRenderer: (params: any) => (
					<Space className={classes.actionButtons} size={8}>
						<Button
							type="link"
							onClick={() => handleEditPickupPoint(params.data)}
						>
							{strings?.table?.edit ?? "Edit"}
						</Button>
						<Button
							type="link"
							onClick={() => openStaffModal(params.data)}
						>
							{strings?.table?.manageStaff ?? "Manage"}
						</Button>
					</Space>
				),
				sortable: false,
				filter: false,
				minWidth: 280,
			},
		];
	}, [countriesMap, handleEditPickupPoint, openStaffModal, strings]);

	const staffColumns = useMemo<ColumnsType<any>>(
		() => [
			{
				title: strings?.staffModal?.driver ?? "Driver",
				dataIndex: "driverName",
				key: "driverName",
			},

			{
				title: strings?.staffModal?.isActive ?? "Active",
				dataIndex: "isActive",
				key: "isActive",
				render: (value: boolean) =>
					value
						? strings?.table?.yes ?? "Yes"
						: strings?.table?.no ?? "No",
			},
			{
				title: strings?.staffModal?.actions ?? "Actions",
				key: "actions",
				render: (_: any, record: PickupPointStaff) => (
					<Space size={8}>
						<Button
							type="link"
							onClick={() => handleEditStaff(record)}
						>
							{strings?.staffModal?.edit ?? "Edit"}
						</Button>
						<Button
							type="link"
							danger
							onClick={() => handleDeleteStaff(record)}
						>
							{strings?.staffModal?.delete ?? "Delete"}
						</Button>
					</Space>
				),
			},
		],
		[handleDeleteStaff, handleEditStaff, strings]
	);

	return (
		<>
			<div className={classes.mainContainer}>
				<div className={classes.heroContainer}>
					{/* <div className={classes.container}> */}
					<div className={classes.header}>
						<h1>{strings?.title ?? "Pickup points"}</h1>
						<div className={classes.buttons}>
							<Button
								className={classes.button}
								onClick={handleAddPickupPoint}
							>
								{strings?.create ?? "Add pickup point"}
							</Button>
						</div>
					</div>
					<div className={`ag-theme-alpine ${classes.gridWrapper}`}>
						<AgGridReact
							ref={gridRef}
							rowData={paginatedData}
							columnDefs={columns}
							defaultColDef={{
								sortable: true,
								filter: true,
								resizable: true,
								flex: 1,
							}}
							domLayout="normal"
							onGridReady={onGridReady}
						/>
					</div>
					<PaginationTable
						current={currentPage}
						pageSize={pageSize}
						total={totalItems}
						startOrderIndex={startItem}
						endOrderIndex={endItem}
						onChange={handlePageChange}
						onShowSizeChange={handlePageSizeChange}
					/>
				</div>
			</div>
			<Modal
				open={isModalVisible}
				title={
					editingPickupPoint
						? strings?.form?.editTitle ?? "Edit pickup point"
						: strings?.form?.createTitle ?? "Add pickup point"
				}
				onCancel={closePickupPointModal}
				onOk={handlePickupPointSubmit}
				okText={strings?.form?.save ?? "Save"}
				cancelText={strings?.form?.cancel ?? "Cancel"}
				destroyOnClose
				okButtonProps={{ style: { backgroundColor: "#4F16C2" } }}
			>
				<Form
					layout="vertical"
					form={form}
					initialValues={{ isActive: true }}
				>
					<Form.Item
						name="name"
						label={strings?.form?.name ?? "Name"}
						rules={[{ required: true }]}
					>
						<Input />
					</Form.Item>
					<Form.Item
						name="countryId"
						label={strings?.form?.country ?? "Country"}
						rules={[{ required: true }]}
					>
						<Select
							options={countries}
							showSearch
							optionFilterProp="label"
						/>
					</Form.Item>
					<Form.Item
						name="addressLine"
						label={strings?.form?.addressLine ?? "Address"}
					>
						<Input />
					</Form.Item>
					<Form.Item
						name="latitude"
						label={strings?.form?.latitude ?? "Latitude"}
					>
						<Input />
					</Form.Item>
					<Form.Item
						name="longitude"
						label={strings?.form?.longitude ?? "Longitude"}
					>
						<Input />
					</Form.Item>
					<Form.Item
						name="mapsUrl"
						label={strings?.form?.mapsUrl ?? "Maps URL"}
					>
						<Input />
					</Form.Item>
					<Form.Item
						name="phonePrimary"
						label={strings?.form?.phonePrimary ?? "Primary phone"}
					>
						<Input />
					</Form.Item>

					<Form.Item
						name="workingHours"
						label={strings?.form?.workingHours ?? "Working hours"}
					>
						<Input />
					</Form.Item>
					<Form.Item
						name="isActive"
						label={strings?.form?.isActive ?? "Active"}
						valuePropName="checked"
					>
						<Switch />
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				open={staffModalVisible}
				onCancel={closeStaffModal}
				footer={null}
				width={720}
				destroyOnClose
				title={
					strings?.staffModal?.title
						? strings.staffModal.title.replace(
								"{name}",
								selectedPickupPoint?.name ?? ""
						  )
						: `Drivers for ${selectedPickupPoint?.name ?? ""}`
				}
				okButtonProps={{ style: { backgroundColor: "#4F16C2" } }}
			>
				<div className={classes.staffHeader}>
					<span>
						{strings?.staffModal?.description ??
							"Manage driver connections for this pickup point."}
					</span>
					<Button className={classes.button} onClick={handleAddStaff}>
						{strings?.staffModal?.add ?? "Add driver"}
					</Button>
				</div>
				<Table
					className={classes.staffTable}
					rowKey="id"
					dataSource={staffTableData}
					columns={staffColumns}
					loading={staffLoading}
					pagination={false}
					locale={{
						emptyText:
							strings?.staffModal?.empty ??
							"No drivers linked to this pickup point yet.",
					}}
				/>
			</Modal>

			<Modal
				open={staffFormVisible}
				onCancel={closeStaffForm}
				onOk={handleStaffSubmit}
				okText={strings?.staffModal?.save ?? "Save"}
				cancelText={strings?.staffModal?.cancel ?? "Cancel"}
				destroyOnClose
				title={
					editingStaff
						? strings?.staffModal?.editTitle ?? "Edit driver"
						: strings?.staffModal?.createTitle ?? "Add driver"
				}
				okButtonProps={{ style: { backgroundColor: "#4F16C2" } }}
			>
				<Form form={staffForm} layout="vertical">
					<Form.Item
						name="userId"
						label={strings?.staffModal?.driver ?? "Driver"}
						rules={[{ required: true }]}
					>
						<Select
							options={drivers}
							showSearch
							optionFilterProp="label"
						/>
					</Form.Item>

					<Form.Item
						name="isActive"
						label={strings?.staffModal?.isActive ?? "Active"}
						valuePropName="checked"
					>
						<Switch />
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
};

export default PickupPoints;
